/**
 * 将表单状态转换为 AB 计算接口请求参数
 */
import { CUSTOM_VALUE } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/components/EventFilter/components/EventFilterContent/config';
import {
  IABExperimentFormState,
  ICalculateAbResultParams,
  IMetric,
  ICustomMetric,
  IEventFilter,
  IEventFilterParameter,
  IExperiment,
  IFilter,
} from '../types';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ALL_VALUE } from '@/pages/datalab/pages/ABExperiment/components/GlobalFilter';

const EVENT_PARAM_SEP = '@__@';
/** numeric 类型参数在 markets 中的后缀，一级选中时才有该值，与 EventPanel/utils NUMERIC_CHILD_SUFFIX 一致 */
const NUMERIC_PARAM_SUFFIX = `${EVENT_PARAM_SEP}numeric`;

const GLOBAL_FILTER_OPERATOR_MAP: Record<string, string> = {
  include: 'in',
  exclude: 'not_in',
  equal: 'eq',
  not_equal: 'neq',
  greater_than: 'gt',
  less_than: 'lt',
  greater_than_or_equal: 'gte',
};

/** 事件参数操作符与接口操作符映射（数值 + 文本） */
const EVENT_NUMERIC_OPERATOR_MAP: Record<string, string> = {
  include: 'in',
  exclude: 'not_in',
  greater: 'gt',
  greaterOrEqual: 'gte',
  less: 'lt',
  lessOrEqual: 'lte',
  equal: 'eq',
  notEqual: 'neq',
  between: 'between',
  // 文本类型操作符（与 EventFilterTextOptions value 对应）
  contains: 'contains',
  notContains: 'not_contains',
  startsWith: 'starts_with',
  endsWith: 'ends_with',
  in: 'in',
  notIn: 'not_in',
};

type TNumericCondition = { operator: string; value: string[]; };

type TEventParameterForFilters = {
  markets?: string[];
  numeric?: Record<string, TNumericCondition>;
};

type TParameterMapEntry = { operator: string; values: (string | number)[]; };

/**
 * 解析 EventPanel 的 `eventParameter`（markets + numeric）为接口 `parameters[]`。
 * 与 `handleEventFilters` 原逻辑一致：跳过 `param@__@numeric` 占位；合并 numeric 条件
 * 并映射 `EVENT_NUMERIC_OPERATOR_MAP`。
 */
function buildEventFilterParametersFromEventParameter(
  eventParameter: TEventParameterForFilters,
): IEventFilterParameter[] {
  const markets: string[] = Array.isArray(eventParameter?.markets)
    ? eventParameter.markets
    : [];
  const numericConditions = eventParameter?.numeric ?? {};
  const parametersMap = new Map<string, TParameterMapEntry>();
  markets.forEach((market: string) => {
    if (typeof market !== 'string' || !market) return;
    const parts = market.split(EVENT_PARAM_SEP);
    const paramKey = parts[0] || '';
    const rawValue = parts[1] || '';
    if (!paramKey || rawValue === 'numeric') return;
    const numVal = Number(rawValue);
    const value: string | number = Number.isNaN(numVal) ? rawValue : numVal;
    if (!parametersMap.has(paramKey)) {
      parametersMap.set(paramKey, { operator: 'in', values: [] });
    }
    const entry = parametersMap.get(paramKey)!;
    if (!entry.values.includes(value)) {
      entry.values.push(value);
    }
  });
  Object.entries(
    numericConditions as Record<string, TNumericCondition>,
  ).forEach(([param_key, cond]) => {
    const numericMarketValue = `${param_key}${NUMERIC_PARAM_SUFFIX}`;
    if (!markets.includes(numericMarketValue)) return;
    const values = cond?.value || [];
    const operator = EVENT_NUMERIC_OPERATOR_MAP[cond.operator] ?? cond.operator;
    parametersMap.set(param_key, { operator, values });
  });
  return Array.from(parametersMap.entries()).map(
    ([param_key, { operator, values }]) => ({
      param_key,
      operator,
      values,
    }),
  );
}

/**
 * 将全局筛选表单项转为接口 IFilter[] 格式
 */
function handleGlobalFilters(globalFilterValue: IColumnInfo[]): IFilter[] {
  if (!Array.isArray(globalFilterValue) || !globalFilterValue.length) {
    return [];
  }
  return globalFilterValue?.map((item: any) => {
    const {
      table_id: tableId,
      filter_method: filterMethod,
      column_type: columnType,
      column_value: columnValue,
      options_all: optionsAll,
      operation,
      column_display: columnDisplay,
      column_display_en: columnDisplayEn,
      isAll,
    } = item || {};
    // 4: string 7: date 8: group_cascade
    const fullMap: Record<number, boolean> = {
      4: isAll || optionsAll?.length === columnValue?.length,
      7: false,
      8: columnValue?.[0] === ALL_VALUE,
    };
    // 是否全选
    const isFull = fullMap?.[filterMethod] || fullMap?.[4];
    // 操作符映射
    const operatorMap: Record<number, string> = {
      4: GLOBAL_FILTER_OPERATOR_MAP[operation] || operation,
      7: 'between',
      8: GLOBAL_FILTER_OPERATOR_MAP[operation] || operation
    };
    // 值映射
    const valuesMap: Record<number, string[]> = {
      4: isFull ? [] : columnValue || [],
      7: columnValue || [],
      8: isFull ? [] : columnValue || [],
    };
    return {
      mapping_id: Number(tableId),
      is_full: isFull,
      column_display: columnDisplay,
      column_display_en: columnDisplayEn,
      column_type: columnType,
      operator: operatorMap?.[filterMethod] || operatorMap?.[4],
      values: valuesMap?.[filterMethod] || valuesMap?.[4],
    };
  });
}

/**
 * 将表单中的自定义指标转为接口 ICustomMetric 格式
 * 表单项：custom_metric_config: { eventName, eventParameter: { markets, numeric, ... }, aggregationType }
 * `parameters` 与事件筛选同源，见 `buildEventFilterParametersFromEventParameter`。
 * mapping_id 取 custom_metric_config.aggregationType
 */
function handleCustomMetrics(data: IColumnInfo): ICustomMetric {
  const { metric_alias: metricAlias, custom_metric_config: customMetricConfig } = data || {};
  const config = customMetricConfig || {
    eventName: '',
    eventParameter: {
      type: '',
      regions: [],
      markets: [],
    },
    aggregationType: '',
  };
  const eventName = config?.eventName || '';
  const eventParameter = config?.eventParameter || {};
  const parameters = buildEventFilterParametersFromEventParameter(
    eventParameter as TEventParameterForFilters,
  );

  const rawMappingId = config?.aggregationType;
  const mappingId =
    rawMappingId !== undefined && rawMappingId !== null && rawMappingId !== ''
      ? Number(rawMappingId)
      : 0;
  return {
    mapping_id: Number.isNaN(mappingId) ? 0 : mappingId,
    is_custom_metric: true,
    custom_metric_def: {
      mapping_id: Number.isNaN(mappingId) ? 0 : mappingId,
      event_name: eventName,
      parameters,
      name: metricAlias || '',
    },
  };
}

function handleMetrics(data: IColumnInfo[]): (IMetric | ICustomMetric)[] {
  return data?.map((item: IColumnInfo) => {
    if (item?.is_custom) {
      return handleCustomMetrics(item);
    }
    return {
      name: item?.metric_alias || '',
      mapping_id: Number(item?.table_id),
      column_display: item?.column_display,
      column_display_en: item?.column_display_en,
      column_type: item?.column_type,
      aggregation_func: item?.aggregate?.toUpperCase?.(),
    };
  }) || [];
}

/**
 * 将事件筛选表单项转为接口 IEventFilter 格式
 * 表单项：eventName, eventParameter: { type, regions, markets }, hoursSinceRegister, occurCountOperator, occurCountValue
 * markets 项格式：param_key@__@value，同 param_key 合并为一条 parameter（operator: in, values: [...]）
 * 发生次数：occurCountOperator + occurCountValue 转为 pv_filter: { operator, values: number[] }
 * 仅当用户类型=新用户时写入 hours_since_register
 */
function handleEventFilters(
  eventFilterValue: IABExperimentFormState['eventFilterValue'],
  userType?: IABExperimentFormState['userType']
): IEventFilter[] {
  if (!Array.isArray(eventFilterValue) || !eventFilterValue.length) {
    return [];
  }
  return eventFilterValue?.map((item: any) => {
    const eventName = item?.eventName || '';
    const eventParameter = item?.eventParameter || {};
    const parameters = buildEventFilterParametersFromEventParameter(
      eventParameter as TEventParameterForFilters,
    );

    const hoursSinceRegisterRaw =
      item?.hoursSinceRegisterCustom && String(item.hoursSinceRegisterCustom).trim()
        ? item.hoursSinceRegisterCustom
        : item?.hoursSinceRegister;
    const hoursSinceRegister =
      hoursSinceRegisterRaw !== undefined && hoursSinceRegisterRaw !== null && hoursSinceRegisterRaw !== ''
        ? Number(hoursSinceRegisterRaw)
        : undefined;
    const hoursSinceRegisterCustom = Number(item?.hoursSinceRegisterCustom);

    const op = item?.occurCountOperator;
    const rawVals = item?.occurCountValue || [];
    const numVals = rawVals
      .map((v: unknown) => (typeof v === 'string' ? Number(v) : Number(v)))
      .filter((n: number) => !Number.isNaN(n));
    const pvFilter =
      op && String(op).trim() && numVals.length > 0
        ? { operator: String(op).trim(), values: numVals }
        : undefined;

    const eventFilter: IEventFilter = {
      event_name: eventName,
      parameters,
    };
    // 注册时长
    if (userType === 'new_user' && hoursSinceRegister !== undefined && !Number.isNaN(hoursSinceRegister)) {
      eventFilter.hours_since_register = hoursSinceRegister;
    }
    // 自定义注册时长
    if (item?.hoursSinceRegister === CUSTOM_VALUE && hoursSinceRegisterCustom !== undefined && !Number.isNaN(hoursSinceRegisterCustom)) {
      eventFilter.hours_since_register = hoursSinceRegisterCustom;
    }
    if (pvFilter) {
      eventFilter.pv_filter = pvFilter;
    }
    return eventFilter;
  })?.filter((item) => item?.event_name !== "") || [];
}

/**
 * 将实验级联值（路径数组 string[][]）转为 IExperiment[]
 * 输入：每条路径 [layer_key, exp_key, group_id?]，按 (layer_key, exp_key) 分组
 * 输出：每组一条 IExperiment，group_ids 为该组所有 path[2] 去重
 */
function handleExperiments(
  experimentFilterValue: IABExperimentFormState['experimentFilterValue']
): IExperiment[] {
  if (!Array.isArray(experimentFilterValue) || experimentFilterValue.length === 0) {
    return [];
  }
  const keySep = '\x00';
  const map = new Map<string, { layerKey: string; expKey: string; groupIds: Set<string>; }>();
  for (const path of experimentFilterValue) {
    const arr = Array.isArray(path) ? path : [];
    const layerKey = typeof arr[0] === 'string' ? arr[0] : String(arr[0] || '');
    const expKey = typeof arr[1] === 'string' ? arr[1] : String(arr[1] || '');
    if (!layerKey || !expKey) continue;
    const key = layerKey + keySep + expKey;
    if (!map.has(key)) {
      map.set(key, { layerKey, expKey, groupIds: new Set<string>() });
    }
    const groupId = arr[2];
    if (groupId !== undefined && groupId !== null && groupId !== '') {
      map.get(key)!.groupIds.add(String(groupId));
    }
  }
  return Array.from(map.values()).map(({ layerKey, expKey, groupIds }) => ({
    layer_key: layerKey,
    exp_key: expKey,
    group_ids: Array.from(groupIds),
  }));
}

/**
 * 根据 gameCode 和表单状态构建 calculateAbResult 的请求参数
 */
export function buildCalculateAbResultParams(
  gameCode: string,
  state: Partial<IABExperimentFormState>
): ICalculateAbResultParams {
  const {
    userType,
    dateRange,
    globalFilterValue,
    globalFilterLogic,
    eventFilterValue,
    eventFilterLogic,
    experimentFilterValue,
    metricFilterValue,
    dimensionFilterValue,
    observationDays = 1,
    limit = 10000,
  } = state;

  return {
    game_code: gameCode,
    user_type: userType || 'new_user',
    date_range: {
      start_date: dateRange?.[0]?.format?.('YYYY-MM-DD') || '',
      end_date: dateRange?.[1]?.format?.('YYYY-MM-DD') || '',
    },
    observation_days: observationDays || 1,
    row_limit: limit,
    filters: handleGlobalFilters(globalFilterValue || []),
    filter_relation: globalFilterLogic,
    event_filters: handleEventFilters(eventFilterValue || [], userType),
    event_relation: eventFilterLogic,
    experiments: handleExperiments(experimentFilterValue || []),
    metrics: handleMetrics(metricFilterValue || []),
    dimensions: dimensionFilterValue?.map((item: any) => ({
      mapping_id: Number(item?.table_id),
      column_display: item?.column_display,
      column_display_en: item?.column_display_en,
      column_type: item?.column_type,
    })),
  };
}
